import styled from '@emotion/styled';
import type { IconName } from '@hotjar/hotsauce';
import { Icon, Stack, Text, tokens } from '@hotjar/hotsauce';
import type { ReactElement } from 'react';
import { useCallback, useEffect, useState } from 'react';
import type { AbstractSingleFilterDropdownProps } from '../../SingleFilterType';
import { GenericFilterBody } from '../GenericFilter/GenericFilterBody';
import { GenericFilterDropdown } from '../GenericFilter/GenericFilterDropdown';
import { GenericFilterFooter } from '../GenericFilter/GenericFilterFooter';
import { GenericFilterHeader } from '../GenericFilter/GenericFilterHeader';
import type { BasicListOption } from '../GenericLargeListFilter/GenericLargeListType';
import { FilterListItem } from '../GenericSubElement/FilterItemList';

export interface ListOption<T> extends BasicListOption<T> {
  icon?: IconName;
  content?: ReactElement;
}

enum ValidationErrorEnum {
  SELECT_ONE_OPTION = 'Select at least one option',
}

interface GenericShortListDropdownProps<T> extends AbstractSingleFilterDropdownProps<T[]> {
  optionList: ListOption<T>[];
  selectAllLabel?: string;
}

const StyledIcon = styled(Icon)`
  margin-right: ${tokens.spacer.x1};
`;

const ContentContainer = styled.div`
  margin-right: ${tokens.spacer.x1};
`;

const OptionLabel = styled.div`
  display: flex;
  align-items: center;
`;

export function GenericShortListDropdown<T>({
  currentValue,
  optionalDefaultValue,
  onApply,
  optionList = [],
  filterName,
  selectAllLabel,
  header,
  renderLabel = true,
  dropdownCss,
}: GenericShortListDropdownProps<T>): JSX.Element {
  const [dirtyValue, setDirtyValue] = useState(currentValue ?? optionalDefaultValue);
  const [validationError, setValidationError] = useState<ValidationErrorEnum>();
  const [areAllOptionsSelected, setAreAllOptionsSelected] = useState(false);
  const [isOneOfOptionsSelected, setIsOneOfOptionsSelected] = useState(false);

  useEffect(() => {
    const isSelected = (option: ListOption<T>) =>
      dirtyValue ? dirtyValue.find((value) => value === option.value) : false;
    setAreAllOptionsSelected(optionList.every((option) => isSelected(option)));
    setIsOneOfOptionsSelected(optionList.some((option) => isSelected(option)));
  }, [setAreAllOptionsSelected, optionList, dirtyValue]);

  const handleChange = useCallback(
    (changedValue) => {
      if (!dirtyValue) {
        setDirtyValue([changedValue]);
      } else {
        setDirtyValue(
          dirtyValue.includes(changedValue)
            ? dirtyValue.filter((dirtyItem) => dirtyItem !== changedValue)
            : [...dirtyValue, changedValue],
        );
      }
      setValidationError(undefined);
    },
    [setDirtyValue, dirtyValue, setValidationError],
  );

  const handleSelectAll = useCallback(() => {
    setDirtyValue(areAllOptionsSelected ? [] : [...optionList.map((option) => option.value)]);
    setValidationError(undefined);
  }, [setDirtyValue, areAllOptionsSelected, setValidationError, optionList]);

  const handleApply = useCallback(() => {
    if (dirtyValue && dirtyValue.length) {
      onApply(dirtyValue);
    } else {
      setValidationError(ValidationErrorEnum.SELECT_ONE_OPTION);
    }
  }, [onApply, dirtyValue]);

  function isChecked(option: ListOption<T>): boolean {
    if (!dirtyValue) {
      return false;
    }
    return dirtyValue.some((dirtyOption) => dirtyOption === option.value);
  }

  return (
    <GenericFilterDropdown ariaLabel={`${filterName} dropdown`} onApply={handleApply}>
      {header != null && <GenericFilterHeader>{header}</GenericFilterHeader>}
      <GenericFilterBody>
        <Stack direction="column" spacing="x1" css={dropdownCss}>
          {selectAllLabel != null && (
            <FilterListItem
              isChecked={areAllOptionsSelected}
              isIndeterminate={isOneOfOptionsSelected && !areAllOptionsSelected}
              id={`${filterName}-dropdown-all`}
              name={selectAllLabel}
              onChange={() => handleSelectAll()}
              value={selectAllLabel}
              isInvalid={!!validationError}
              labelContent={<OptionLabel>{selectAllLabel}</OptionLabel>}
            />
          )}
          {optionList.map((option) => {
            return (
              <FilterListItem
                isChecked={isChecked(option)}
                id={`${filterName}-dropdown-${option.value}`}
                key={option.label}
                name={option.label}
                onChange={() => handleChange(option.value)}
                value={`${option.value}`}
                isInvalid={!!validationError}
                labelContent={
                  <OptionLabel>
                    {option.content && <ContentContainer>{option.content}</ContentContainer>}
                    {option.icon && <StyledIcon icon={option.icon} />}
                    {renderLabel && option.label}
                  </OptionLabel>
                }
              />
            );
          })}
          {!!validationError && <Text textColor="text_error">{validationError}</Text>}
        </Stack>
      </GenericFilterBody>
      <GenericFilterFooter />
    </GenericFilterDropdown>
  );
}
